import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import httpClient from '../Api/axios';

const GlobalEnquiryIcon = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formRef = useRef(null);

  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [showAutoPopup, setShowAutoPopup] = useState(false);
  const [isAutoPopupClosed, setIsAutoPopupClosed] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Get service options from translations
  const serviceOptions = t('globalEnquiry.form.serviceOptions', { returnObjects: true });

  // Handle click outside to close form
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (formRef.current && !formRef.current.contains(event.target)) {
        handleCloseForm();
      }
    };

    if (showEnquiryForm || showAutoPopup) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'auto';
    };
  }, [showEnquiryForm, showAutoPopup]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Submit form to backend API
  const handleSubmitEnquiry = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await httpClient.post('/api/enquiry', {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        phone: formData.phone,
        service: formData.service,
        message: formData.message
      });

      if (response.data.success) {
        console.log('✅ Enquiry submitted:', response.data);

        setSubmitStatus({
          type: 'success',
          message: 'Enquiry submitted successfully! Confirmation email sent.'
        });

        setFormData({
          name: '', email: '', company: '', phone: '', service: '', message: ''
        });

        navigate('/thank-you');

        setTimeout(() => {
          handleCloseForm();
          setSubmitStatus(null);
        }, 2000);
      } else {
        throw new Error(response.data.message || 'Submission failed');
      }
    } catch (error) {
      console.error('❌ Enquiry submission error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Something went wrong. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle enquiry icon click
  const handleEnquiryClick = () => {
    setShowEnquiryForm(true);
    setShowAutoPopup(false);
  };

  // Close form
  const handleCloseForm = () => {
    if (!isSubmitting) {
      setShowEnquiryForm(false);
      setShowAutoPopup(false);
    }
  };

  // Close auto popup
  const handleCloseAutoPopup = () => {
    setShowAutoPopup(false);
    setIsAutoPopupClosed(true);
    localStorage.setItem('autoPopupClosed', 'true');
  };

  // Render enquiry form
  const renderEnquiryForm = (isAutoPopup = false) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-3 bg-black/60 backdrop-blur-sm"
      onClick={isAutoPopup ? handleCloseAutoPopup : handleCloseForm}
    >
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 50, opacity: 0 }}
        transition={{ type: "spring", damping: 25 }}
        className="relative w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
        ref={formRef}
      >
        <div className="bg-gradient-to-br from-white via-white to-red-50 rounded-xl shadow-xl overflow-hidden border border-gray-200 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="relative bg-gradient-to-r from-red-600 to-red-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">
                  {isAutoPopup
                    ? t('globalEnquiry.autoPopup.title')
                    : t('globalEnquiry.manualPopup.title')}
                </h3>
                <p className="text-red-100 text-sm mt-0.5">
                  {isAutoPopup
                    ? t('globalEnquiry.autoPopup.subtitle')
                    : t('globalEnquiry.manualPopup.subtitle')}
                </p>
              </div>
              <button
                onClick={isAutoPopup ? handleCloseAutoPopup : handleCloseForm}
                className="text-white hover:text-gray-200 transition-colors p-1"
                disabled={isSubmitting}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmitEnquiry} className="p-4 md:p-5">
            <div className="grid grid-cols-1 gap-4">
              {/* Name */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  {t('globalEnquiry.form.labels.name')} {t('globalEnquiry.form.labels.required')}
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  disabled={isSubmitting}
                  placeholder={t('globalEnquiry.form.placeholders.name')}
                />
              </div>

              {/* Email */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  {t('globalEnquiry.form.labels.email')} {t('globalEnquiry.form.labels.required')}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  disabled={isSubmitting}
                  placeholder={t('globalEnquiry.form.placeholders.email')}
                />
              </div>

              {/* Company */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  {t('globalEnquiry.form.labels.company')}
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  disabled={isSubmitting}
                  placeholder={t('globalEnquiry.form.placeholders.company')}
                />
              </div>

              {/* Phone */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  {t('globalEnquiry.form.labels.phone')}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  disabled={isSubmitting}
                  placeholder={t('globalEnquiry.form.placeholders.phone')}
                />
              </div>

              {/* Service */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  {t('globalEnquiry.form.labels.service')} {t('globalEnquiry.form.labels.required')}
                </label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  disabled={isSubmitting}
                >
                  <option value="">{t('globalEnquiry.form.placeholders.service')}</option>
                  {serviceOptions.map((service, idx) => (
                    <option key={idx} value={service}>{service}</option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  {t('globalEnquiry.form.labels.message')} {t('globalEnquiry.form.labels.required')}
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="3"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  disabled={isSubmitting}
                  placeholder={t('globalEnquiry.form.placeholders.message')}
                />
              </div>
            </div>

            {/* Status */}
            {submitStatus && (
              <div className={`mt-3 p-3 text-sm rounded-lg ${submitStatus.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                {submitStatus.message}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full mt-4 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 text-sm rounded-lg transition-colors disabled:opacity-50"
            >
              {isSubmitting
                ? t('globalEnquiry.form.buttons.sending')
                : t('globalEnquiry.form.buttons.submit')}
            </button>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <>
      {/* Floating Enquiry Icon */}
      <motion.button
        initial={{ opacity: 0, scale: 0, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ delay: 2, type: "spring" }}
        onClick={handleEnquiryClick}
        className="fixed bottom-20 right-24 z-[9998] w-14 h-14 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-full shadow-xl hover:shadow-2xl hover:from-red-700 hover:to-red-800 transition-all duration-300 group"
        style={{ boxShadow: '0 8px 25px rgba(220, 38, 38, 0.4)' }}
      >
        <div className="relative flex items-center justify-center w-full h-full">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span className="absolute -top-1 -right-1 bg-white text-red-600 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
            !
          </span>
        </div>
      </motion.button>

      {/* Manual Enquiry Form */}
      <AnimatePresence>
        {showEnquiryForm && renderEnquiryForm(false)}
      </AnimatePresence>
    </>
  );
};

export default GlobalEnquiryIcon;
